import React, { useState, useEffect } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
}

const ArtisticComingSoon: React.FC = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [hologramText, setHologramText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const phases = [
    {
      title: "NEURAL NETWORK INITIALIZATION",
      subtitle: "Loading AI Consciousness...",
      progress: 23,
      color: "#00ff88"
    },
    {
      title: "LEARNING ALGORITHMS ACTIVE",
      subtitle: "Analyzing Student Patterns...",
      progress: 56,
      color: "#00aaff"
    },
    {
      title: "PERSONALITY MATRIX CALIBRATED",
      subtitle: "Developing Teaching Style...",
      progress: 89,
      color: "#ff6b6b"
    },
    {
      title: "AI TUTOR READY",
      subtitle: "Awaiting Final Launch Sequence...",
      progress: 97,
      color: "#ffd93d"
    }
  ];

  useEffect(() => {
    // Initialize particles
    const newParticles: Particle[] = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      size: Math.random() * 3 + 1,
      color: `hsl(${Math.random() * 360}, 70%, 60%)`
    }));
    setParticles(newParticles);

    // Phase cycling
    const phaseTimer = setInterval(() => {
      setCurrentPhase(prev => (prev + 1) % 4); // Fixed number since phases array is constant
    }, 4000);

    // Hologram typing effect
    const messageTimer = setInterval(() => {
      const messages = [
        "Hello, I'm your future AI Tutor 🤖",
        "I can see you're curious about learning...",
        "Soon, I'll understand exactly how you think",
        "We'll solve problems together, step by step",
        "I'm learning about you right now...",
        "Your personalized tutor is almost ready!"
      ];
      const randomMessage = messages[Math.floor(Math.random() * messages.length)];
      typeMessage(randomMessage);
    }, 8000);

    // Initial message
    setTimeout(() => {
      typeMessage("Hello, I'm your future AI Tutor 🤖");
    }, 2000);

    return () => {
      clearInterval(phaseTimer);
      clearInterval(messageTimer);
    };
  }, []);

  useEffect(() => {
    // Animate particles
    const animateParticles = () => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        x: (particle.x + particle.vx + 100) % 100,
        y: (particle.y + particle.vy + 100) % 100
      })));
    };

    const particleTimer = setInterval(animateParticles, 50);
    return () => clearInterval(particleTimer);
  }, []);

  const typeMessage = async (message: string) => {
    setIsTyping(true);
    setHologramText('');
    
    for (let i = 0; i <= message.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 50));
      setHologramText(message.substring(0, i));
    }
    
    setIsTyping(false);
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setHologramText('');
    }, 3000);
  };

  return (
    <div className="position-relative overflow-hidden" style={{ minHeight: '100vh', height: 'auto', background: 'linear-gradient(135deg, #0a0a0a, #1a1a2e, #16213e)' }}>
      {/* Animated Background Particles */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="position-absolute rounded-circle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              opacity: 0.7,
              animation: `pulse 2s infinite`,
              filter: 'blur(0.5px)'
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="position-relative z-index-2 d-flex align-items-center justify-content-center min-vh-100 text-white p-4">
        <div className="text-center" style={{ maxWidth: '600px' }}>
          
          {/* 3D Holographic AI Brain */}
          <div className="mb-5 position-relative">
            <div 
              className="d-inline-block position-relative"
              style={{
                perspective: '1000px',
                animation: 'float 6s ease-in-out infinite'
              }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center position-relative"
                style={{
                  width: '120px',
                  height: '120px',
                  background: `linear-gradient(45deg, ${phases[currentPhase].color}40, ${phases[currentPhase].color}80)`,
                  border: `3px solid ${phases[currentPhase].color}`,
                  boxShadow: `0 0 40px ${phases[currentPhase].color}60, inset 0 0 40px ${phases[currentPhase].color}30`,
                  transform: 'rotateY(0deg)',
                  animation: 'rotate3d 10s linear infinite',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <i 
                  className="fas fa-brain fa-3x"
                  style={{ 
                    color: phases[currentPhase].color,
                    filter: `drop-shadow(0 0 10px ${phases[currentPhase].color})`,
                    animation: 'pulse 2s infinite'
                  }}
                />
                
                {/* Orbiting Elements */}
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="position-absolute rounded-circle"
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: phases[currentPhase].color,
                      top: '50%',
                      left: '50%',
                      transform: `translate(-50%, -50%) rotate(${i * 120}deg) translateX(80px)`,
                      animation: `orbit ${3 + i}s linear infinite`,
                      boxShadow: `0 0 10px ${phases[currentPhase].color}`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Phase Information */}
          <div className="mb-4">
            <h1 
              className="fw-bold mb-3"
              style={{
                fontSize: '2.5rem',
                color: phases[currentPhase].color,
                textShadow: `0 0 20px ${phases[currentPhase].color}`,
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}
            >
              {phases[currentPhase].title}
            </h1>
            <p 
              className="fs-5 mb-4"
              style={{
                color: phases[currentPhase].color + 'aa',
                textShadow: `0 0 10px ${phases[currentPhase].color}50`
              }}
            >
              {phases[currentPhase].subtitle}
            </p>
          </div>

          {/* Progress Bar with Glow Effect */}
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ color: phases[currentPhase].color, fontFamily: 'monospace' }}>
                SYSTEM STATUS
              </span>
              <span style={{ color: phases[currentPhase].color, fontFamily: 'monospace' }}>
                {phases[currentPhase].progress}%
              </span>
            </div>
            <div 
              className="position-relative"
              style={{
                height: '20px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '10px',
                overflow: 'hidden',
                border: `1px solid ${phases[currentPhase].color}50`
              }}
            >
              <div
                className="h-100 position-relative"
                style={{
                  width: `${phases[currentPhase].progress}%`,
                  background: `linear-gradient(90deg, ${phases[currentPhase].color}, ${phases[currentPhase].color}aa)`,
                  borderRadius: '10px',
                  boxShadow: `0 0 20px ${phases[currentPhase].color}80`,
                  transition: 'all 1s ease',
                  animation: 'shimmer 2s infinite'
                }}
              />
            </div>
          </div>

          {/* Holographic Message Display */}
          <div className="mb-5">
            <div 
              className="p-4 rounded-4 position-relative"
              style={{
                background: 'rgba(0,255,136,0.1)',
                border: '1px solid #00ff8850',
                backdropFilter: 'blur(10px)',
                minHeight: '80px',
                boxShadow: '0 0 30px #00ff8830'
              }}
            >
              <div className="d-flex align-items-center justify-content-center h-100">
                {hologramText && (
                  <p 
                    className="mb-0 fs-6"
                    style={{
                      color: '#00ff88',
                      fontFamily: 'monospace',
                      textShadow: '0 0 10px #00ff88',
                      letterSpacing: '1px'
                    }}
                  >
                    {hologramText}
                    {isTyping && <span className="text-warning">|</span>}
                  </p>
                )}
                {!hologramText && !isTyping && (
                  <div className="d-flex gap-1">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="rounded-circle"
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#00ff88',
                          animation: `bounce 1.4s infinite ease-in-out ${i * 0.16}s`
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-white-50 mb-3" style={{ fontFamily: 'monospace' }}>
              NEURAL PATHWAYS ESTABLISHING...
            </p>
            <button
              className="btn btn-lg px-5 py-3 me-3"
              style={{
                background: `linear-gradient(45deg, ${phases[currentPhase].color}, ${phases[currentPhase].color}aa)`,
                border: `2px solid ${phases[currentPhase].color}`,
                color: 'white',
                borderRadius: '25px',
                boxShadow: `0 0 20px ${phases[currentPhase].color}50`,
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}
            >
              <i className="fas fa-satellite-dish me-2"></i>
              CONNECT TO AI
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes rotate3d {
            0% { transform: rotateY(0deg) rotateX(0deg); }
            25% { transform: rotateY(90deg) rotateX(0deg); }
            50% { transform: rotateY(180deg) rotateX(10deg); }
            75% { transform: rotateY(270deg) rotateX(0deg); }
            100% { transform: rotateY(360deg) rotateX(0deg); }
          }
          
          @keyframes orbit {
            0% { transform: translate(-50%, -50%) rotate(0deg) translateX(80px) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg) translateX(80px) rotate(-360deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
          
          @keyframes shimmer {
            0% { background-position: -200px 0; }
            100% { background-position: calc(200px + 100%) 0; }
          }
          
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
        `
      }} />
    </div>
  );
};

export default ArtisticComingSoon;